import { Patterns } from '../../shared/validation/patterns';
import {
  IPromise,
  IValidator,
  MessageLevel,
  Validator,
} from '../../shared/validation/validators';
import { ValidationUtils } from '../../shared/validation/validation-runner';
import { FieldState } from '../../shared/models/field-state';
import { Mode } from '../customer-edit-agents/mode.enum';
import { ApplicationMessageSource } from '../../core/models/application-message-source';
import { MessageType } from '../../core/models/enum/message-type';
import { ApplicationMessage } from '../../core/models/application-message';
import { CustomMessageService } from '../../core/message.service';
import { CustomerService } from '../shared/services/customer.service';
import { Utils } from '../../shared/utils';
import { CustomerAgentDetailsModel } from '../shared/models/customer-agent-details-model';
import { CommonLookupItem } from '../../shared/models/common-lookup-item.model';
import { LookupService } from '../../shared/services/lookup.service';
import { CustomerModel } from '../shared/models/customer-model';
import {
  Component,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  EventEmitter,
} from '@angular/core';
import { EmailType } from '../../shared/models/email-type.enum';
import { SmsTypeModel } from '@admin/shared/models/sms-type-model';
import { CustomerAgentSmsSetting } from '../shared/models/customer-agent-sms-setting';
import { ConfigurationService } from '@admin/shared/services/configuration.service';
import { CustomerAgentSaveResultModel } from '../shared/models/customer-agent-save-result-model';
import { SmsType } from '@admin/shared/models/sms-type.enum';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'hr-customer-agent-editor',
  templateUrl: './customer-agent-editor.component.html',
  styleUrls: ['./customer-agent-editor.component.less'],
  standalone: false,
})
export class CustomerAgentEditorComponent implements OnInit {
  @Output()
  public onUpdate: EventEmitter<boolean> = new EventEmitter<boolean>();
  @Input()
  public currentEditingAgent: CustomerAgentDetailsModel;
  @Input()
  public mode: Mode;
  public Mode: any = Mode;

  public emailTypes: CommonLookupItem<number, string>[];
  public agentSwitchedOnEmails: { [emailTypeId: number]: boolean };
  public EmailTypeEnum = EmailType;

  public smsTypes: SmsTypeModel[];
  public smsCharsets: CommonLookupItem<number, string>[];
  public smsSettings: { [smsTypeId: number]: CustomerAgentSmsSetting };

  public customerAgent: CustomerAgentDetailsModel;

  public primaryLanguage: string;
  public alternativeLanguage: string;

  public fieldStates: {
    name: FieldState;
    surname: FieldState;
    email: FieldState;
    mobileNumber: FieldState;
    mobileNumber2: FieldState;
    phoneNumber: FieldState;
    phoneNumberExtension: FieldState;
    allPhoneNumbers: FieldState;
    position: FieldState;
    reasonId: FieldState;
  };

  public get field() {
    return this.fieldStates;
  }

  constructor(
    private customerService: CustomerService,
    private messageService: CustomMessageService,
    private config: ConfigurationService,
    private lookupService: LookupService,
    private translationService: TranslateService
  ) {
    this.customerAgent = new CustomerAgentDetailsModel();
    this.emailTypes = [];
    this.smsTypes = [];
    this.smsCharsets = [];
    this.smsSettings = {};
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['currentEditingAgent'].currentValue != null) {
      this.customerAgent = Utils.cloneObject(
        this.currentEditingAgent,
        CustomerAgentDetailsModel
      );
      this.initAgentSwitchedOnEmails();
      this.initializeFieldsState();
    }
  }

  onSaveClick() {
    let varlidators = this.getValidators();
    this.invokeValidation(varlidators, () => {
      this.setAgentStoppedEmailsSubmitData();
      this.customerService
        .saveCustomerAgent(this.customerAgent)
        .subscribe((result) => {
          this.processSaveResult(result);
        });
    });
  }

  processSaveResult(result: CustomerAgentSaveResultModel) {
    if (result.success) {
      this.messageService.pushMessage(<ApplicationMessage>{
        type: MessageType.Success,
        sourceId: ApplicationMessageSource.global,
        summary: '',
        detail: this.translationService.instant('message.SaveMessage'),
      });
      this.onUpdate.emit(true);
    } else {
      this.messageService.pushMessage(<ApplicationMessage>{
        type: MessageType.Error,
        sourceId: ApplicationMessageSource.global,
        summary: '',
        detail: this.translationService.instant('message.' + result.error),
      });
    }
  }

  onCancelClick() {
    this.onUpdate.emit(true);
  }

  setAgentStoppedEmailsSubmitData() {
    this.customerAgent.stoppedEmails = [];
    for (let key in this.agentSwitchedOnEmails) {
      if (!this.agentSwitchedOnEmails[key]) {
        this.customerAgent.stoppedEmails.push(Number(key));
      }
    }
  }

  public initializeFieldsState() {
    this.fieldStates = {
      name: new FieldState(() => this.customerAgent && this.customerAgent.name),
      surname: new FieldState(
        () => this.customerAgent && this.customerAgent.surname
      ),
      email: new FieldState(
        () => this.customerAgent && this.customerAgent.email
      ),
      phoneNumber: new FieldState(
        () => this.customerAgent && this.customerAgent.phoneNumber
      ),
      phoneNumberExtension: new FieldState(
        () => this.customerAgent && this.customerAgent.phoneNumberExtension
      ),
      mobileNumber: new FieldState(
        () => this.customerAgent && this.customerAgent.mobilePhoneNumber
      ),
      mobileNumber2: new FieldState(
        () => this.customerAgent && this.customerAgent.mobilePhoneNumber2
      ),
      allPhoneNumbers: new FieldState(() => this.customerAgent),
      position: new FieldState(
        () => this.customerAgent && this.customerAgent.position
      ),
      reasonId: new FieldState(
        () =>
          this.customerAgent &&
          this.customerAgent.smsSettings[SmsType.DebtRemind].reasonId
      ),
    };
  }

  public invokeValidation(validators: IValidator[], onValid?: () => void) {
    ValidationUtils.invokeValidation(
      validators,
      this.getFieldStatesObject(),
      onValid
    );
  }

  private getFieldStatesObject(): { [index: string]: FieldState } {
    let result: { [index: string]: FieldState } = {};
    for (let p in this.fieldStates) {
      if (this.fieldStates.hasOwnProperty(p)) {
        result[this.fieldStates[p].fieldId] = this.fieldStates[p];
      }
    }

    return result;
  }

  private getValidators() {
    let field = this.field;
    let customerAgent = this.customerAgent;

    let emptyMessage = this.translationService.instant(
      'message.ValueLeftEmpty'
    );
    let incorrectFormat = this.translationService.instant(
      'message.IncorrectFormat'
    );
    let incorrectPhone = this.translationService.instant(
      'message.PhoneNumberIncorrectFormat'
    );
    let incorrectMinLength = this.translationService.instant(
      'message.IncorrectMinLength'
    ) as string;
    let isAlreadyInUse = this.translationService.instant(
      'message.IsAlreadyInUse'
    );
    let noPhoneNumberProvided = this.translationService.instant(
      'message.FillAtLeastOnePhoneNumber'
    );

    let result = [
      Validator.forRequired(field.name, () => customerAgent.name, emptyMessage),
      Validator.forPattern(
        field.name,
        () => customerAgent.name,
        Patterns.personName,
        incorrectFormat
      ),
      Validator.forRequired(
        field.surname,
        () => customerAgent.surname,
        emptyMessage
      ),
      Validator.forPattern(
        field.surname,
        () => customerAgent.surname,
        Patterns.personName,
        incorrectFormat
      ),
      Validator.forRequired(
        field.email,
        () => customerAgent.email,
        emptyMessage
      ),
      Validator.forPattern(
        field.email,
        () => customerAgent.email,
        Patterns.emailNew,
        incorrectFormat
      ),

      this.mode == Mode.Add
        ? Validator.forAsync(
            field.email,
            () => {
              var p = this.customerService.validateEmail(customerAgent.email);
              return <IPromise>{
                catch: (error) => p.catch(error),
                then: (data) => p.then(data),
                finally: () => {
                  alert('todo');
                },
              };
            },
            (response: any) => {
              var isValid = response.isSuccess;
              return {
                message: isValid ? null : isAlreadyInUse,
                level: isValid ? null : MessageLevel.Error,
                isValid: isValid,
              };
            }
          )
        : Validator.forBypass(field.email),
      Validator.forNotRequired(
        field.phoneNumber,
        () => customerAgent.phoneNumber
      ),
      Validator.forPattern(
        field.phoneNumber,
        () => customerAgent.phoneNumber,
        Patterns.phoneNumber,
        incorrectPhone
      ),
      Validator.forNotRequired(
        field.phoneNumberExtension,
        () => customerAgent.phoneNumberExtension
      ),
      Validator.forPattern(
        field.phoneNumberExtension,
        () => customerAgent.phoneNumberExtension,
        Patterns.phoneNumberExtension,
        incorrectPhone
      ),
      Validator.forNotRequired(
        field.mobileNumber,
        () => customerAgent.mobilePhoneNumber
      ),
      Validator.forPattern(
        field.mobileNumber,
        () => customerAgent.mobilePhoneNumber,
        Patterns.phoneNumber,
        incorrectPhone
      ),
      Validator.forNotRequired(
        field.mobileNumber2,
        () => customerAgent.mobilePhoneNumber2
      ),
      Validator.forPattern(
        field.mobileNumber2,
        () => customerAgent.mobilePhoneNumber2,
        Patterns.phoneNumber,
        incorrectPhone
      ),
      Validator.forRequired(
        field.allPhoneNumbers,
        () =>
          customerAgent.phoneNumber ||
          customerAgent.mobilePhoneNumber ||
          customerAgent.mobilePhoneNumber2,
        noPhoneNumberProvided
      ),
      Validator.forNotRequired(field.position, () => customerAgent.position),
      Validator.forMinLength(
        field.position,
        () => customerAgent.position,
        2,
        incorrectMinLength.replace('{len}', '2')
      ),
      Validator.forPattern(
        field.position,
        () => customerAgent.position,
        Patterns.noNumbers,
        incorrectFormat
      ),
    ];

    if (!customerAgent.smsSettings[SmsType.DebtRemind].isOn)
      result.push(
        Validator.forPredicate(
          field.reasonId,
          () => customerAgent.smsSettings[SmsType.DebtRemind].reasonId != null,
          emptyMessage
        )
      );
    return result;
  }

  ngOnInit() {
    const config = this.config.getCachedConfig();
    const lookupData = this.lookupService.getCachedLookupData();
    this.emailTypes = lookupData.emailTypes;
    this.smsTypes = lookupData.smsTypes;
    this.smsCharsets = lookupData.messageLanguageCharsets;
    this.initAgentSwitchedOnEmails();
    this.initAgentSmsSettings(config.smsDefaultLanguageCharsetId);
    this.primaryLanguage = config.primaryLanguage;
    this.alternativeLanguage = config.alternativeLanguage;

    if (this.mode == Mode.Add) {
      this.customerAgent.languageId = this.primaryLanguage;
    }
  }

  private initAgentSwitchedOnEmails() {
    this.agentSwitchedOnEmails = {};
    this.emailTypes.forEach((t) => {
      this.agentSwitchedOnEmails[t.value] =
        this.mode === Mode.Edit &&
        this.customerAgent.stoppedEmails.filter((e) => e == t.value).length ===
          0;
    });
  }

  private initAgentSmsSettings(defaultCharsetId: number) {
    if (this.mode == Mode.Add) {
      this.smsTypes.forEach((type) => {
        this.customerAgent.smsSettings[type.smsTypeId] =
          new CustomerAgentSmsSetting(
            type.smsTypeId,
            true,
            defaultCharsetId,
            null
          );
      });
    }
    this.smsSettings = this.customerAgent.smsSettings;
  }
}
